// src/utils/jwt.js — JWT expiry utilities (pure JS, no dependencies)

/**
 * Check if a JWT token is expired (or will expire within marginSeconds).
 * @param {string} token - JWT token string
 * @param {number} [marginSeconds=60] - Safety margin in seconds
 * @returns {boolean} true if token is expired or will expire within margin
 */
export function isTokenExpired(token, marginSeconds = 60) {
    try {
        const expiresAt = getTokenExpiresAt(token);
        if (expiresAt === null) return true; // No exp claim — treat as expired
        return Date.now() >= expiresAt - marginSeconds * 1000;
    } catch {
        return true; // Malformed token — treat as expired
    }
}

/**
 * Get the expiry timestamp (in milliseconds) from a JWT token.
 * @param {string} token - JWT token string
 * @returns {number|null} Expiry time in ms, or null if no exp claim
 */
export function getTokenExpiresAt(token) {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;

        // Decode base64url payload
        let payload = parts[1];
        payload = payload.replace(/-/g, '+').replace(/_/g, '/');
        // Pad to multiple of 4
        while (payload.length % 4 !== 0) {
            payload += '=';
        }

        const decoded = JSON.parse(atob(payload));
        if (typeof decoded.exp !== 'number') return null;

        return decoded.exp * 1000; // Convert seconds to milliseconds
    } catch {
        return null;
    }
}
